/**
 * Edit Community Page
 * Multi-step form with accordion sections for editing an existing community
 */

import { zodResolver } from "@hookform/resolvers/zod"
import type { AxiosError } from "axios"
import { ChevronsUpDown, MoreVertical, Plus, Trash2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { TagsInput } from "@/components/forms/tagsInput"
import { SelectCommunityUsersModal } from "@/components/modals/selectCommunityUsersModal"
import { BackButton } from "@/components/shared/BackButton"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import {
  COMMUNITY_IMAGE_LIMITS,
  CommunityMediaSection,
} from "@/features/communities/components/communityMediaSection"
import { useCommunity } from "@/features/communities/hooks/useCommunity"
import { useCommunityMembersInfinite } from "@/features/communities/hooks/useCommunityMembersInfinite"
import { useUpdateCommunity } from "@/features/communities/hooks/useCommunityMutations"
import { useCommunityRoles } from "@/features/communities/hooks/useCommunityRoles"
import {
  type CreateCommunityFormData,
  createCommunitySchema,
} from "@/features/communities/schemas/community.schema"
import type { CommunityUserWithRole, LocationData } from "@/features/communities/types"
import {
  type SectionValidationResult,
  validateBasicSection,
  validateGuidelinesSection,
  validatePrivacySection,
  validateTagsSection,
  validateUsersSection,
} from "@/features/communities/utils/accordionValidation"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useTranslation } from "@/language"
import type { CommunityTag } from "../types/community-detail"

// Helper to convert uppercase to Capital Case
const toCapitalCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

// Map backend uppercase values to frontend titlecase labels (static, outside component)
const BACKEND_TO_FRONTEND_MAP: Record<string, string> = {
  OWNERS: "Owners",
  MODERATORS: "Moderators",
  RITUALISTS: "Ritualists",
  MEMBERS: "Members",
}

export function EditCommunityScreen() {
  const { t } = useTranslation("communities")
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: community, isLoading, error: fetchError } = useCommunity(id || "")

  // Fetch all community members with infinite scroll for the Admin & Roles section
  const {
    data: membersData,
    isLoading: membersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCommunityMembersInfinite(id || "")

  // Flatten all pages into a single array of all members
  const allMembers = membersData?.pages.flatMap(page => page.members) ?? []

  // Infinite scroll - triggers fetchNextPage when user scrolls to bottom
  const loadMoreRef = useInfiniteScroll({
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    hasMore: !!hasNextPage,
    disabled: isFetchingNextPage,
  })

  // Get current user from auth store
  const currentUser = useAuthStore(state => state.user)

  // Initialize React Hook Form with Zod validation
  const form = useForm<CreateCommunityFormData>({
    resolver: zodResolver(createCommunitySchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      handle: "",
      type: "public",
      isOrganization: false,
      address: "",
      description: "",
      websiteUrl: "",
      twitterUrl: "",
      instagramUrl: "",
      facebookUrl: "",
      guidelines: "",
      tags: [],
      privacySettings: {
        addToRituals: ["Ritualists"],
        comment: ["Everyone"],
      },
      selectedUsers: [],
    },
  })

  const {
    register,
    formState: { errors, isValid },
    trigger,
    clearErrors,
    setValue,
    reset,
    control,
    watch,
  } = form

  // Watch only the specific fields we need for validation (prevents infinite loop)
  const {
    name,
    handle,
    description,
    guidelines,
    privacySettings: formPrivacySettings,
  } = useWatch({
    control,
  })

  // Fetch community roles using React Query hook
  const { data: communityRoles = [] } = useCommunityRoles()

  // Get OWNER role ID from community roles
  const ownerRole = communityRoles.find(r => r.title === "OWNER")
  const ownerRoleId = ownerRole?.id

  // Role options for dropdowns - map from fetched roles
  const roleOptions = communityRoles.map(role => ({
    value: role.id,
    label: toCapitalCase(role.title),
  }))

  // Original state for change tracking
  const [originalTags, setOriginalTags] = useState<string[]>([])
  const [originalTagsWithIds, setOriginalTagsWithIds] = useState<
    Array<{ id: string; text: string }>
  >([])
  const [originalUsers, setOriginalUsers] = useState<CommunityUserWithRole[]>([])

  // Current state
  const [tags, setTags] = useState<string[]>([])
  const [tagsWithIds, setTagsWithIds] = useState<Array<{ id: string; text: string }>>([])
  const [selectedUsers, setSelectedUsers] = useState<CommunityUserWithRole[]>([])
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  // Track removed user IDs to filter them from the displayed list
  const [removedUserIds, setRemovedUserIds] = useState<Set<string>>(new Set())

  // Selected location data
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)

  // Community type state
  const [communityType, setCommunityType] = useState<"public" | "private">("public")

  // Privacy settings state - only keep addToRituals and comment
  const [privacySettings, setPrivacySettings] = useState({
    addToRituals: ["Ritualists"],
    comment: ["Everyone"],
  })

  // Media counts state for accordion summary
  const [mediaCounts, setMediaCounts] = useState({ images: 0, videos: 0 })

  // Combine allMembers with newly added users from selectedUsers (who aren't in API response yet)
  // Transform selectedUsers to match the member format expected by the UI
  const newlyAddedUsers = selectedUsers
    .filter(su => !allMembers.some(am => am.id === su.userId))
    .map(su => ({
      id: su.userId,
      name: su.name,
      username: su.username,
      email: su.email,
      coverImage: su.profilePictureUrl,
      roleIds: su.roleIds,
      roleId: su.roleId,
      communityBuilder: su.communityBuilder,
    }))
  const allMembersWithAdded = [...allMembers, ...newlyAddedUsers]

  // Video URL state
  // Video URLs with thumbnails - for multiple videos
  interface VideoUrlEntry {
    id: string
    url: string
    thumbnail: string
    title?: string
    isInvalid?: boolean
  }
  const [originalVideos, setOriginalVideos] = useState<VideoUrlEntry[]>([])
  const [editedVideos, setEditedVideos] = useState<VideoUrlEntry[]>([])
  const [deletedVideoIds, setDeletedVideoIds] = useState<string[]>([])
  const [newVideoUrls, setNewVideoUrls] = useState<VideoUrlEntry[]>([])
  const [editedVideoIds, setEditedVideoIds] = useState<string[]>([])

  // Cover image status for validation
  const [hasCoverImage, setHasCoverImage] = useState(false)
  const [mediaError, setMediaError] = useState<string | null>(null)

  // Memoized privacy settings handlers to prevent infinite re-renders
  const handleAddToRitualsChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, addToRituals: selected }))
  }, [])

  const handleCommentChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, comment: selected }))
  }, [])

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Mutation hook with automatic cache invalidation
  const updateCommunityMutation = useUpdateCommunity(id || "")

  // Accordion ref
  const accordionRef = useRef<AccordionRef>(null)

  // Check if form is valid for enabling/disabling submit button
  const isFormDisabled = !isValid

  // Initialize form with community data
  useEffect(() => {
    if (community) {
      // Set form values
      reset({
        name: community.name || "",
        handle: community.handle || "",
        type: (community.type.toLowerCase() || "public") as "public" | "private",
        isOrganization: (community as unknown as Record<string, unknown>).is_organization === true,
        address: community.streetAddress || "",
        description: community.description || "",
        websiteUrl: community.websiteUrl || "",
        twitterUrl: community.twitterUrl || "",
        instagramUrl: community.instagramUrl || "",
        facebookUrl: community.facebookUrl || "",
        guidelines: community.guidelines || "",
        tags: [],
        privacySettings: {
          addToRituals: ["Ritualists"],
          comment: ["Everyone"],
        },
        selectedUsers: [],
      })

      // Set tags - map tag objects to strings (TagsInput expects string[])
      // Also track tag IDs for removal (backend expects UUIDs, not names)
      // Handle both string[] and CommunityTag[] formats
      const tagsData = community.tags.map((tag: string | CommunityTag) =>
        typeof tag === "string" ? { id: "", text: tag } : { id: tag.id, text: tag.tagText },
      )
      const tagNames = tagsData.map(t => t.text)
      setTags(tagNames)
      setOriginalTags(tagNames)
      setTagsWithIds(tagsData)
      setOriginalTagsWithIds(tagsData)
      setValue("tags", tagNames)

      // Set location
      if (community.lat && community.long) {
        setSelectedLocation({
          address: community.streetAddress || "",
          lat: community.lat,
          lng: community.long,
          city: community.city,
          state: community.state,
          country: community.country,
          zipCode: community.zipcode || undefined,
        })
      }

      // Set community type
      setCommunityType((community.type.toLowerCase() || "public") as "public" | "private")

      // Set video URLs from community videos (CommunityVideo table - YouTube/Vimeo URLs)
      if (community.communityVideos && community.communityVideos.length > 0) {
        const videos: VideoUrlEntry[] = community.communityVideos.map(
          (v: {
            id: string
            videoUrl: string
            thumbnailUrl: string | null
            title: string | null
          }) => ({
            id: v.id,
            url: v.videoUrl,
            thumbnail: v.thumbnailUrl || "",
            title: v.title || "",
            isProcessed: true,
          }),
        )
        setOriginalVideos(videos)
        setEditedVideos(videos)
      }

      // Set users - mark current user as Owner
      const users = community.members.map(m => {
        const isCurrentUser = currentUser?.id === m.userId
        return {
          userId: m.userId,
          name: m.name,
          email: m.email || "",
          username: null,
          profilePictureUrl: m.profilePictureUrl || null,
          roleIds: m.roleIds && m.roleIds.length > 0 ? m.roleIds : m.roleId ? [m.roleId] : [],
          roleId: m.roleId || "", // Keep for backward compatibility
          communityBuilder: m.communityBuilder || false,
          isCurrentUser: isCurrentUser,
        }
      })
      setSelectedUsers(users)
      setOriginalUsers(users)
      setValue("selectedUsers", users)

      // Parse privacy settings - backend sends uppercase values, map to frontend titlecase
      const getSettingValue = (key: string) => {
        const value = (community as unknown as Record<string, unknown>)[key] as string[] | undefined
        if (!Array.isArray(value)) return ["Moderators"]
        return value.map(v => BACKEND_TO_FRONTEND_MAP[v] || v)
      }

      const newPrivacySettings = {
        addToRituals: getSettingValue("whoCanAddCommunityToRituals"),
        comment: getSettingValue("whoCanComment"),
      }
      setPrivacySettings(newPrivacySettings)
    }
  }, [community, reset, setValue, currentUser?.id])

  // Handlers
  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)

    // For new tags added by user, we don't have an ID yet
    // That's fine - they go in the "add" array, not "remove"
    // We still add them to tagsWithIds with empty ID for consistency
    setTagsWithIds(prev => [...prev, { id: "", text: tag }])

    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)

    // Also remove from tagsWithIds to track which tags were removed
    const updatedTagsWithIds = tagsWithIds.filter(t => t.text !== tagToRemove)
    setTagsWithIds(updatedTagsWithIds)

    trigger("tags")
  }

  const handleAddUsers = (users: CommunityUserWithRole[]) => {
    // Filter out current user if already present (they're added by default as Owner)
    const newUsers = users.filter(u => u.userId !== currentUser?.id)
    const updated = [...selectedUsers, ...newUsers]
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  // Handle removing a user - prevent removing current user (Owner)
  const handleRemoveUser = (userId: string) => {
    // Don't allow removing the current user (Owner)
    if (userId === currentUser?.id) {
      return
    }
    // Remove from selectedUsers (for form submission tracking)
    const updated = selectedUsers.filter(u => u.userId !== userId)
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    // Track removed user ID (to hide from UI)
    setRemovedUserIds(prev => new Set(prev).add(userId))
    trigger("selectedUsers")
  }

  // Handle updating user roles - prevent changing current user's role
  const handleUpdateUserRoles = (userId: string, newRoleIds: string[]) => {
    // Don't allow changing the current user's (Owner) role
    if (userId === currentUser?.id) {
      return
    }

    // Check if user already exists in selectedUsers
    const existingUser = selectedUsers.find(u => u.userId === userId)

    let updated: CommunityUserWithRole[]
    if (existingUser) {
      // User exists - update their role
      updated = selectedUsers.map(u => (u.userId === userId ? { ...u, roleIds: newRoleIds } : u))
    } else {
      // User doesn't exist in selectedUsers - need to find them in allMembers and add
      const member = allMembers.find(m => m.id === userId)
      if (member) {
        const newUser: CommunityUserWithRole = {
          userId: member.id,
          name: member.name || null,
          email: "",
          username: member.username,
          profilePictureUrl: member.coverImage || null,
          roleIds: newRoleIds,
          roleId: newRoleIds[0] || "",
          isCurrentUser: false,
        }
        updated = [...selectedUsers, newUser]
      } else {
        return // Can't find user, bail
      }
    }

    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  // Format media summary for accordion
  const formatMediaSummary = useCallback((imageCount: number, videoCount: number): string => {
    const parts: string[] = []
    if (imageCount > 0) {
      parts.push(`${imageCount} image${imageCount !== 1 ? "s" : ""}`)
    }
    if (videoCount > 0) {
      parts.push(`${videoCount} video${videoCount !== 1 ? "s" : ""}`)
    }
    return parts.length > 0 ? parts.join(", ") : "No media added"
  }, [])

  // Handle media count changes from CommunityMediaSection
  const handleMediaCountChange = useCallback(
    (imageCount: number, videoCount: number) => {
      setMediaCounts({ images: imageCount, videos: videoCount + newVideoUrls.length })
    },
    [newVideoUrls.length],
  )

  // Handle video URLs changes from CommunityMediaSection
  const handleVideoUrlsChange = useCallback(
    (videos: VideoUrlEntry[]) => {
      // Determine which are new vs existing
      const existingIds = originalVideos.map(v => v.id)
      const newVids = videos.filter(v => !existingIds.includes(v.id))

      // Track deleted videos
      const currentIds = videos.map(v => v.id)
      const deleted = originalVideos
        .filter(v => !currentIds.includes(v.id))
        .map(v => v.id)
        .filter(id => !deletedVideoIds.includes(id))

      // Track edited videos (same ID but different URL/thumbnail)
      const edited = videos
        .filter(v => {
          const original = originalVideos.find(ov => ov.id === v.id)
          if (!original) return false
          return (
            original.url !== v.url ||
            original.thumbnail !== v.thumbnail ||
            original.title !== v.title
          )
        })
        .map(v => v.id)
      setNewVideoUrls(newVids)
      setEditedVideos(videos)
      setDeletedVideoIds(prev => [...new Set([...prev, ...deleted])])
      setEditedVideoIds(prev => [...new Set([...prev, ...edited])])
    },
    [originalVideos, deletedVideoIds],
  )

  // Handle cover image status changes from CommunityMediaSection
  const handleCoverImageChange = useCallback((hasCover: boolean) => {
    setHasCoverImage(hasCover)
    if (hasCover) {
      setMediaError(null)
    }
  }, [])

  // Validate all accordion sections - use useMemo to prevent infinite loop
  const sectionValidation = useMemo(() => {
    if (!community) {
      return {}
    }

    // Create a minimal data object with only the fields needed for validation
    const validationData: Partial<CreateCommunityFormData> = {
      name: name ?? "",
      handle: handle ?? "",
      description: description ?? "",
      guidelines: guidelines ?? "",
      privacySettings: (formPrivacySettings ?? {
        visibility: ["Everyone"],
        join: ["Everyone"],
        addPosts: ["Admins", "Ritualists"],
        addToRituals: ["Ritualists"],
        comment: ["Everyone"],
      }) as CreateCommunityFormData["privacySettings"],
    }

    return {
      "community-info": validateBasicSection(
        validationData as CreateCommunityFormData,
        selectedLocation,
      ),
      description: validateTagsSection(validationData as CreateCommunityFormData, selectedLocation),
      "upload-media": {
        isComplete: mediaCounts.images > 0 && hasCoverImage,
        summary: formatMediaSummary(mediaCounts.images, mediaCounts.videos + newVideoUrls.length),
        displayName: "Upload Media",
      },
      "users-roles": validateUsersSection(validationData as CreateCommunityFormData, selectedUsers),
      privacy: validatePrivacySection(validationData as CreateCommunityFormData),
      guidelines: validateGuidelinesSection(validationData as CreateCommunityFormData),
    } as Record<string, SectionValidationResult>
  }, [
    community,
    selectedUsers,
    selectedLocation,
    name,
    handle,
    description,
    guidelines,
    formPrivacySettings,
    mediaCounts,
    hasCoverImage,
    formatMediaSummary,
    newVideoUrls,
  ])

  // Transform array for API - map frontend titlecase to backend uppercase
  const transformArray = (arr: string[]): string[] => {
    const mapping: Record<string, string> = {
      Owners: "OWNERS",
      Moderators: "MODERATORS",
      Ritualists: "RITUALISTS",
      Members: "MEMBERS",
    }
    return arr.map(item => mapping[item] || item.toUpperCase().replace(" ", "_"))
  }

  // Handle form submission
  const handleSubmit = async (data: CreateCommunityFormData) => {
    if (!id) {
      toast.error("Community ID is required")
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Calculate tag changes
      const addedTags = tags.filter(t => !originalTags.includes(t))

      // Removed tags - use tag IDs from tracking state
      // Backend expects UUIDs in the remove array, not tag names
      const removedTagIds = originalTagsWithIds
        .filter(
          originalTag => !tagsWithIds.some(currentTag => currentTag.text === originalTag.text),
        )
        .map(t => t.id)
        .filter(id => id !== "") // Filter out tags with empty IDs (newly added tags)

      // Calculate user changes
      const addedUsers = selectedUsers.filter(
        su => !originalUsers.find(ou => ou.userId === su.userId),
      )
      const updatedUsers = originalUsers
        .filter(ou => {
          const current = selectedUsers.find(cu => cu.userId === ou.userId)
          // Compare arrays - check if roleIds have changed
          const oldRoleIds =
            ou.roleIds && ou.roleIds.length > 0 ? ou.roleIds : ou.roleId ? [ou.roleId] : []
          const newRoleIds =
            current?.roleIds && current.roleIds.length > 0
              ? current.roleIds
              : current?.roleId
                ? [current.roleId]
                : []
          return JSON.stringify(oldRoleIds) !== JSON.stringify(newRoleIds)
        })
        .map(ou => {
          const current = selectedUsers.find(cu => cu.userId === ou.userId)
          const roleIdsArray =
            current?.roleIds && current.roleIds.length > 0
              ? current.roleIds
              : current?.roleId
                ? [current.roleId]
                : []
          return {
            userId: ou.userId,
            roleIds: roleIdsArray,
          }
        })
      // Use tracked removedUserIds state (tracks users removed via UI)
      const removedUserIdsArray = Array.from(removedUserIds)

      // Prepare update data
      const updateData: Record<string, unknown> = {
        name: data.name,
        community_handle: data.handle,
        type: communityType.toUpperCase(),
        is_organization: data.isOrganization,
        street_address: selectedLocation?.address,
        city: selectedLocation?.city,
        state: selectedLocation?.state,
        country: selectedLocation?.country,
        zip_code: selectedLocation?.zipCode,
        lat: selectedLocation?.lat,
        long: selectedLocation?.lng,
        description: data.description,
        website_url: data.websiteUrl || undefined,
        twitter_url: data.twitterUrl || undefined,
        instagram_url: data.instagramUrl || undefined,
        facebook_url: data.facebookUrl || undefined,
        videos:
          deletedVideoIds.length > 0 || newVideoUrls.length > 0 || editedVideoIds.length > 0
            ? {
                delete: deletedVideoIds.length > 0 ? deletedVideoIds : undefined,
                add:
                  newVideoUrls.length > 0
                    ? newVideoUrls.map(v => ({
                        video_url: v.url,
                        thumbnail_url: v.thumbnail,
                        title: v.title || "",
                      }))
                    : undefined,
                edit:
                  editedVideoIds.length > 0
                    ? editedVideos
                        .filter(v => editedVideoIds.includes(v.id))
                        .map(v => ({
                          id: v.id,
                          video_url: v.url,
                          thumbnail_url: v.thumbnail,
                          title: v.title || "",
                        }))
                    : undefined,
              }
            : undefined,
        guidelines: data.guidelines,
        who_can_add_community_to_rituals: transformArray(privacySettings.addToRituals),
        who_can_comment: transformArray(privacySettings.comment),
      }

      // Add tag changes if any
      if (addedTags.length > 0 || removedTagIds.length > 0) {
        updateData.tags = {
          add: addedTags.length > 0 ? addedTags : undefined,
          remove: removedTagIds.length > 0 ? removedTagIds : undefined,
        }
      }

      // Add user changes if any
      const filteredUpdateUsers = updatedUsers.filter(u => u.roleIds && u.roleIds.length > 0)
      if (
        addedUsers.length > 0 ||
        filteredUpdateUsers.length > 0 ||
        removedUserIdsArray.length > 0
      ) {
        updateData.users = {
          add:
            addedUsers.length > 0
              ? addedUsers.map(u => ({
                  user_id: u.userId,
                  role_ids:
                    u.roleIds && u.roleIds.length > 0 ? u.roleIds : u.roleId ? [u.roleId] : [],
                }))
              : undefined,
          update:
            filteredUpdateUsers.length > 0
              ? filteredUpdateUsers.map(u => ({
                  user_id: u.userId,
                  role_ids: u.roleIds,
                }))
              : undefined,
          remove: removedUserIdsArray.length > 0 ? removedUserIdsArray : undefined,
        }
      }

      // Call mutation with automatic cache invalidation
      await updateCommunityMutation.mutateAsync(updateData)

      // Success - redirect to community view page
      toast.success(t("editCommunity.successMessage"))
      navigate(`/communities/${id}`)
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ message: string }>
      const errorMessage = axiosError.response?.data?.message || t("editCommunity.errorMessage")
      setSubmitError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t("editCommunity.loading")}</p>
      </div>
    )
  }

  if (fetchError || !community) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">{t("editCommunity.loadingError")}</p>
      </div>
    )
  }

  // Accordion items data
  const accordionItems: AccordionItem[] = [
    {
      id: "community-info",
      title: t("editCommunity.communityInfo"),
      showNextButton: true,
      isComplete: sectionValidation["community-info"]?.isComplete,
      summary: sectionValidation["community-info"]?.summary,
      displayName: sectionValidation["community-info"]?.displayName,
      content: (
        <div className="space-y-4 mb-5">
          <p className="text-sm mb-7 mt-2 ms-1 text-body">{t("editCommunity.basicInfo")}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {/* Name Field */}
            <CustomInput
              {...register("name")}
              id="community-name"
              label={t("editCommunity.name")}
              required
              placeholder={t("editCommunity.placeholders.name")}
              error={errors.name?.message}
              onClearError={() => clearErrors("name")}
            />

            {/* Community Handle Field */}
            <CustomInput
              {...register("handle")}
              id="community-handle"
              label={t("editCommunity.handle")}
              required
              placeholder={t("editCommunity.placeholders.handle")}
              error={errors.handle?.message}
              onClearError={() => clearErrors("handle")}
            />
          </div>

          <div className="flex items-center gap-3 ms-1">
            <Checkbox
              id="is-organization"
              checked={watch("isOrganization")}
              onCheckedChange={checked => setValue("isOrganization", checked === true)}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <label
              htmlFor="is-organization"
              className="text-sm text-body cursor-pointer select-none"
            >
              This community represents a company or organization
            </label>
          </div>
        </div>
      ),
    },
    {
      id: "description",
      title: t("editCommunity.descriptionSocialLinks"),
      showNextButton: true,
      isComplete: sectionValidation["description"]?.isComplete,
      summary: sectionValidation["description"]?.summary,
      displayName: sectionValidation["description"]?.displayName,
      content: (
        <div className="space-y-4">
          <p className="text-[14px] font-monteserat mb-7 mt-2 text-body">
            {t("editCommunity.descriptionOverview")}
          </p>

          {/* Description Field */}
          <CustomTextarea
            {...register("description")}
            id="description"
            label={t("editCommunity.description")}
            required
            placeholder={t("editCommunity.placeholders.description")}
            error={errors.description?.message}
            onClearError={() => clearErrors("description")}
            rows={4}
          />

          {/* Tags */}
          <TagsInput
            id="tags"
            label={t("editCommunity.tags")}
            tags={tags}
            required={true}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            placeholder={t("editCommunity.placeholders.tags")}
            enableSearch={true}
          />

          {/* Location */}
          <AutocompleteInput
            mode="full"
            label={t("editCommunity.address")}
            placeholder={t("editCommunity.placeholders.location")}
            onLocationSelect={setSelectedLocation}
            value={selectedLocation?.address}
          />

          {/* Social Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomInput
              {...register("websiteUrl")}
              id="website"
              label={t("editCommunity.website")}
              placeholder={t("editCommunity.placeholders.website")}
              error={errors.websiteUrl?.message}
              onClearError={() => clearErrors("websiteUrl")}
            />

            <CustomInput
              {...register("twitterUrl")}
              id="twitter"
              label={t("editCommunity.twitter")}
              placeholder={t("editCommunity.placeholders.twitter")}
              error={errors.twitterUrl?.message}
              onClearError={() => clearErrors("twitterUrl")}
            />

            <CustomInput
              {...register("instagramUrl")}
              id="instagram"
              label={t("editCommunity.instagram")}
              placeholder={t("editCommunity.placeholders.instagram")}
              error={errors.instagramUrl?.message}
              onClearError={() => clearErrors("instagramUrl")}
            />

            <CustomInput
              {...register("facebookUrl")}
              id="facebook"
              label={t("editCommunity.facebook")}
              placeholder={t("editCommunity.placeholders.facebook")}
              error={errors.facebookUrl?.message}
              onClearError={() => clearErrors("facebookUrl")}
            />
          </div>
        </div>
      ),
    },
    {
      id: "upload-media",
      title: t("editCommunity.uploadMedia"),
      showNextButton: true,
      isComplete: sectionValidation["upload-media"]?.isComplete,
      summary: sectionValidation["upload-media"]?.summary,
      displayName: sectionValidation["upload-media"]?.displayName,
      content: (
        <div className="space-y-4">
          <p className="text-[14px] ms-1 font-monteserat loading-[20px] text-body mb-5 mt-2">
            {t("editCommunity.uploadMediaDescription")}
          </p>

          {id && (
            <CommunityMediaSection
              communityId={id}
              onMediaCountChange={handleMediaCountChange}
              onCoverImageChange={handleCoverImageChange}
              initialVideoUrls={editedVideos}
              onVideoUrlsChange={handleVideoUrlsChange}
              maxFiles={COMMUNITY_IMAGE_LIMITS.MAX_IMAGES}
              maxFileSize={COMMUNITY_IMAGE_LIMITS.MAX_FILE_SIZE}
              enableAiGeneration
              aiEntityType="community"
              aiEntityContext={{
                name,
                description,
                type: communityType,
              }}
            />
          )}
          {mediaError && <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>}
        </div>
      ),
    },
    {
      id: "users-roles",
      title: t("editCommunity.usersRoles"),
      showNextButton: true,
      isComplete: sectionValidation["users-roles"]?.isComplete,
      summary: sectionValidation["users-roles"]?.summary,
      displayName: sectionValidation["users-roles"]?.displayName,
      userPreviews: sectionValidation["users-roles"]?.userPreviews,
      totalCount: allMembersWithAdded.length - removedUserIds.size,
      content: (
        <div className="space-y-4">
          <p className="text-sm mb-7 text-body mt-2">{t("editCommunity.usersRolesDescription")}</p>

          {/* Users List - now showing all community members with infinite scroll */}
          {(allMembers.length > 0 || membersLoading) && (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {/* Loading state */}
              {membersLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              )}

              {/* All Members - render from infinite scroll data + newly added users, excluding removed users */}
              {allMembersWithAdded
                .filter(member => !removedUserIds.has(member.id))
                .map(member => {
                  // Find this user in selectedUsers to get their role info, or use member's roleIds from API
                  const selectedUser = selectedUsers.find(su => su.userId === member.id)
                  // Use roleIds from selectedUsers if available, otherwise from API response
                  const memberRoleIds = selectedUser?.roleIds || member.roleIds || []

                  return (
                    <div key={member.id} className="flex items-center gap-4">
                      {/* Avatar - 48px with beige background */}
                      <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {member.coverImage ? (
                          <img
                            src={member.coverImage}
                            alt={member.name || "User"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-semibold">
                            {(member.name || member.username || "U")[0].toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {member.name || t("editCommunity.noName")}
                        </p>
                        <p className="text-sm truncate">
                          {member.username ? `@${member.username}` : member.name}
                        </p>
                      </div>

                      {/* Role Display - Show differently based on user status */}
                      {member.communityBuilder ? (
                        // Current user (Owner) - show as Owner badge, disabled
                        <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 opacity-50 cursor-not-allowed">
                          <span className="truncate">Owner</span>
                          <ChevronsUpDown size={16} strokeWidth={2.5} />
                        </div>
                      ) : (
                        // All other members - show role dropdown with full controls
                        <MultiSelectDropdown
                          options={roleOptions.map(o => o.label)}
                          selectedOptions={
                            memberRoleIds && memberRoleIds.length > 0
                              ? roleOptions
                                  .filter(o => memberRoleIds.includes(o.value))
                                  .map(o => o.label)
                              : ["Ritualist"]
                          }
                          onChange={labels => {
                            const selectedRoleIds = roleOptions
                              .filter(o => labels.includes(o.label))
                              .map(o => o.value)
                            handleUpdateUserRoles(member.id, selectedRoleIds)
                          }}
                        />
                      )}

                      {/* Action Icons - Hidden for community creator */}
                      {!member.communityBuilder && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRemoveUser(member.id)}
                              className="text-red focus:text-red"
                            >
                              <Trash2 size={16} />
                              {t("editCommunity.removeUser")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  )
                })}

              {/* Infinite scroll sentinel - triggers fetchNextPage when visible */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="flex items-center justify-center py-4">
                  {isFetchingNextPage ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  ) : (
                    <span className="text-sm text-gray-500">Scroll for more...</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            {/* Invite Organizer Button */}
            <Button
              variant="ghost"
              onClick={() => setIsUserModalOpen(true)}
              className="flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
            >
              <Plus size={18} />
              {t("editCommunity.addMore")}
            </Button>
          </div>

          {/* Users Modal */}
          <SelectCommunityUsersModal
            isOpen={isUserModalOpen}
            onClose={() => setIsUserModalOpen(false)}
            onAddUsers={handleAddUsers}
            existingUserIds={selectedUsers.map(u => u.userId)}
            communityId={id}
            communityRoles={communityRoles}
            currentUser={
              currentUser
                ? {
                    id: currentUser.id,
                    name: currentUser.name,
                    email: currentUser.email,
                    username: currentUser.username,
                    profilePictureUrl:
                      currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)
                        ?.filePath || currentUser.profilePictureUrl,
                    profilePhotos: currentUser.profile_photos,
                  }
                : undefined
            }
            ownerRoleId={ownerRoleId}
          />
        </div>
      ),
    },
    {
      id: "guidelines",
      title: t("createCommunity.guidelines"),
      showNextButton: true,
      isComplete: sectionValidation["guidelines"]?.isComplete,
      summary: sectionValidation["guidelines"]?.summary,
      displayName: sectionValidation["guidelines"]?.displayName,
      content: (
        <div className="space-y-4 mb-5">
          <p className="text-sm mb-7 text-body">{t("editCommunity.guidelinesDescription")}</p>

          <CustomTextarea
            {...register("guidelines")}
            id="guidelines"
            label={t("editCommunity.guidelines")}
            placeholder={t("editCommunity.placeholders.guidelines")}
            rows={4}
          />
          {errors.guidelines && (
            <p className="text-sm text-destructive">{errors.guidelines.message}</p>
          )}
        </div>
      ),
    },
    {
      id: "privacy",
      title: t("editCommunity.privacySettings"),
      showNextButton: true,
      isComplete: sectionValidation["privacy"]?.isComplete,
      summary: sectionValidation["privacy"]?.summary,
      displayName: sectionValidation["privacy"]?.displayName,
      content: (
        <div className="space-y-3">
          <p className="text-body mt-[6px] text-[14px] loading-[20px]">
            {t("createCommunity.privacyDescription")}
          </p>

          {/* Community Type Selection */}
          <div className="flex text=[12px] items-center justify-between">
            <label className="font-semibold text-primary block">{t("createCommunity.type")}</label>
            <Select
              value={communityType}
              onValueChange={value => setCommunityType(value as "public" | "private")}
            >
              <SelectTrigger className="font-semibold text-[14px] p-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public - Anyone can join</SelectItem>
                <SelectItem value="private">Private - Invite only / Request to join</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            {/* Who can add/accept rituals to community */}
            <MultiSelectDropdown
              label={t("editCommunity.whoCanAddToRituals")}
              options={["Moderators", "Ritualists", "Members"]}
              selectedOptions={privacySettings.addToRituals}
              onChange={handleAddToRitualsChange}
            />

            {/* Who can comment */}
            <MultiSelectDropdown
              label={t("editCommunity.whoCanComment")}
              options={["Moderators", "Ritualists", "Members"]}
              selectedOptions={privacySettings.comment}
              onChange={handleCommentChange}
            />
          </div>
        </div>
      ),
    },
  ]

  const handleNext = async (currentIndex: number) => {
    // Trigger validation for current section
    const items = [
      "community-info",
      "description",
      "upload-media",
      "users-roles",
      "privacy",
      "guidelines",
    ] as const
    const currentItemId = items[currentIndex]

    // Custom validation for media section
    if (currentItemId === "upload-media") {
      if (mediaCounts.images === 0) {
        setMediaError(t("editCommunity.imageRequired"))
        return false
      }
      if (!hasCoverImage) {
        setMediaError(t("editCommunity.selectCoverImage"))
        return false
      }
      // Check for invalid video URLs
      const invalidVideos = editedVideos.filter(v => v.isInvalid)
      if (invalidVideos.length > 0) {
        setMediaError(t("editCommunity.invalidVideoUrl"))
        return false
      }
      setMediaError(null)
      return true
    }

    // Custom validation for users section
    if (currentItemId === "users-roles") {
      if (selectedUsers.length === 0) {
        toast.error(t("editCommunity.memberRequired"))
        return false
      }
    }

    // biome-ignore lint/suspicious/noExplicitAny: trigger doesn't support const assertion types
    const isValid = await trigger(currentItemId as any)
    return isValid
  }

  return (
    <div className="min-h-screen bg-background ">
      <div className="max-w-360 mx-auto px-5 md:px-17.5 py-10">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton className="bg-card border-card" label={t("communityHeader.back")} />
        </div>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Accordions */}
          <div className="lg:col-span-2 space-y-6">
            <Accordion
              ref={accordionRef}
              items={accordionItems}
              defaultOpenIndex={0}
              allowMultiple={false}
              onNextClick={handleNext}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Submit Button */}
            <div className="rounded-lg border-1 bg-card border-card p-6">
              <h3 className="text-xl font-semibold mb-4 text-primary">
                {t("editCommunity.reviewPublish")}
              </h3>
              <p className="text-sm text-body mb-4">{t("editCommunity.reviewDescription")}</p>
              <Button
                onClick={form.handleSubmit(handleSubmit)}
                disabled={isSubmitting || isFormDisabled}
                className="w-full"
              >
                {isSubmitting ? t("editCommunity.updating") : t("editCommunity.updateButton")}
              </Button>
              {submitError && <p className="text-sm text-destructive mt-2">{submitError}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
